import * as THREE from "three";

import {
  buretteReadingMl,
  phenolphthaleinAppearance,
  titrationPh,
} from "./models";
import type { TitrationGuidanceTarget } from "./TitrationGuidedController";
import type { TitrationSessionState } from "./session";

export function pickTitrationInteraction(
  hits: THREE.Intersection[],
  root: THREE.Object3D
) {
  for (const hit of hits) {
    // Outline lines use a world-space picking threshold and are decorative.
    if (!(hit.object as THREE.Mesh).isMesh) continue;
    let object: THREE.Object3D | null = hit.object;
    while (object && object !== root) {
      if (object.userData.interaction?.id) return object;
      object = object.parent;
    }
  }
  return null;
}

const BURETTE_DISPLAY_CAPACITY_ML = 25;
const BURETTE_LIQUID_HEIGHT_M = 0.45;
const BURETTE_LIQUID_BOTTOM_M = 0.36;
const BURETTE_SCALE_TOP_M = 0.81;
const BURETTE_SCALE_BOTTOM_M = 0.36;
const BURETTE_OUTLET_Y_M = 0.19;
const GLASS_EDGE_COLOR = 0x244a50;
const FOCUSED_EDGE_COLOR = 0x8ee8f0;

export type TitrationOverlayFocus = TitrationGuidanceTarget;

export type TitrationOverlayWorldAnchors = {
  meniscus: THREE.Vector3;
  tip: THREE.Vector3;
  tap: THREE.Vector3;
  flaskNeck: THREE.Vector3;
  flaskBody: THREE.Vector3;
  record: THREE.Vector3;
};

export type TitrationWorkstationVisualState = {
  buretteFillFraction: number;
  tapOpenFraction: number;
  bubbleVisible: boolean;
  flaskAppearance: "clear" | "colourless" | "faint pink" | "pink";
  flaskColor: number;
  flaskOpacity: number;
  ph: number;
};

export type TitrationWorkstationDebugState = TitrationWorkstationVisualState & {
  rootPosition: [number, number, number];
  rootVisible: boolean;
  overlayFocus: TitrationOverlayFocus;
  glassMaterialKind: string;
  glassTransmission: number;
  glassOpacity: number;
  glassEdgeOpacity: number;
  buretteLiquidOpacity: number;
  flaskSurfaceOpacity: number;
  contactShadowVisible: boolean;
  scaleLabelCount: number;
  motion: {
    dropVisible: boolean;
    dropY: number;
    swirlRemaining: number;
    flaskOffset: number[];
    surfaceTilt: number;
  };
};

export function deriveTitrationWorkstationVisualState(
  session: TitrationSessionState
): TitrationWorkstationVisualState {
  const buretteFillFraction = THREE.MathUtils.clamp(
    1 -
      buretteReadingMl(session.dispensedMl, true) / BURETTE_DISPLAY_CAPACITY_ML,
    0,
    1
  );
  const tapOpenFraction = THREE.MathUtils.clamp(session.tapOpenFraction, 0, 1);
  const ph = titrationPh(session.dispensedMl, session.airBubblePresent);
  const appearance = phenolphthaleinAppearance(ph);

  if (!session.indicatorAdded || !session.swirled) {
    return {
      buretteFillFraction,
      tapOpenFraction,
      bubbleVisible: session.airBubblePresent,
      flaskAppearance: "clear",
      flaskColor: 0xaedce2,
      flaskOpacity: 0.76,
      ph,
    };
  }

  if (appearance.label === "faint pink") {
    return {
      buretteFillFraction,
      tapOpenFraction,
      bubbleVisible: session.airBubblePresent,
      flaskAppearance: "faint pink",
      flaskColor: 0xff9fc6,
      flaskOpacity: 0.78,
      ph,
    };
  }

  if (appearance.label === "pink") {
    return {
      buretteFillFraction,
      tapOpenFraction,
      bubbleVisible: session.airBubblePresent,
      flaskAppearance: "pink",
      flaskColor: 0xec4899,
      flaskOpacity: 0.84,
      ph,
    };
  }

  return {
    buretteFillFraction,
    tapOpenFraction,
    bubbleVisible: session.airBubblePresent,
    flaskAppearance: "colourless",
    flaskColor: 0xaedce2,
    flaskOpacity: 0.74,
    ph,
  };
}

function mesh(
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  name: string
) {
  const object = new THREE.Mesh(geometry, material);
  object.name = name;
  object.castShadow = true;
  object.receiveShadow = true;
  return object;
}

type FocusEdge = {
  material: THREE.LineBasicMaterial;
  group: "tip" | "flask" | "reading" | "neutral";
  normalOpacity: number;
};

function addReadableEdges(
  object: THREE.Mesh,
  group: FocusEdge["group"],
  normalOpacity: number,
  focusEdges: FocusEdge[]
) {
  const material = new THREE.LineBasicMaterial({
    color: GLASS_EDGE_COLOR,
    transparent: true,
    opacity: normalOpacity,
  });
  const edges = new THREE.LineSegments(
    new THREE.EdgesGeometry(object.geometry, 18),
    material
  );
  edges.name = `${object.name}Edges`;
  object.add(edges);
  focusEdges.push({ material, group, normalOpacity });
  return edges;
}

function createScaleLabel(
  text: string,
  ownedTextures: THREE.Texture[]
): THREE.Sprite | null {
  if (typeof document === "undefined") return null;
  if (
    typeof navigator !== "undefined" &&
    navigator.userAgent.toLowerCase().includes("jsdom")
  ) {
    return null;
  }

  try {
    const canvas = document.createElement("canvas");
    canvas.width = 96;
    canvas.height = 48;
    const context = canvas.getContext("2d");
    if (!context) return null;

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = "#243b36";
    context.font = "700 34px system-ui, sans-serif";
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(text, canvas.width / 2, canvas.height / 2 + 1);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    ownedTextures.push(texture);

    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthWrite: false,
        depthTest: true,
      })
    );
    sprite.name = `TitrationBuretteScaleLabel-${text}`;
    sprite.scale.set(0.062, 0.031, 1);
    return sprite;
  } catch {
    return null;
  }
}

export function createTitrationWorkstation3D() {
  const root = new THREE.Group();
  root.name = "TitrationWorkstation";
  root.userData = {
    experimentId: "titration",
    station: "Wet Lab student bench",
  };

  const ownedTextures: THREE.Texture[] = [];
  const focusEdges: FocusEdge[] = [];
  let overlayFocus: TitrationOverlayFocus = null;

  const metal = new THREE.MeshStandardMaterial({
    color: 0x39464a,
    roughness: 0.34,
    metalness: 0.72,
  });
  const darkMetal = new THREE.MeshStandardMaterial({
    color: 0xb96c28,
    roughness: 0.48,
    metalness: 0.12,
  });
  const scaleMaterial = new THREE.MeshBasicMaterial({ color: 0x243b36 });
  const glass = new THREE.MeshPhysicalMaterial({
    color: 0xd4edf0,
    metalness: 0,
    roughness: 0.07,
    transmission: 0.48,
    thickness: 0.025,
    ior: 1.46,
    clearcoat: 0.38,
    clearcoatRoughness: 0.1,
    transparent: true,
    opacity: 0.82,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  const buretteLiquidMaterial = new THREE.MeshPhysicalMaterial({
    color: 0xf3f8f7,
    metalness: 0,
    roughness: 0.08,
    transmission: 0.72,
    thickness: 0.017,
    ior: 1.33,
    opacity: 1,
    depthWrite: false,
    emissive: 0x000000,
    emissiveIntensity: 0,
  });
  const flaskLiquidMaterial = new THREE.MeshPhysicalMaterial({
    color: 0xaedce2,
    metalness: 0,
    roughness: 0.14,
    transmission: 0.04,
    transparent: true,
    opacity: 0.76,
    depthWrite: false,
    emissive: 0x000000,
    emissiveIntensity: 0,
  });
  const flaskLiquidSurfaceMaterial = new THREE.MeshPhysicalMaterial({
    color: 0x6faeb9,
    metalness: 0,
    roughness: 0.08,
    transparent: true,
    opacity: 0.9,
    side: THREE.DoubleSide,
    depthWrite: false,
    emissive: 0x000000,
    emissiveIntensity: 0,
  });
  const whiteTileMaterial = new THREE.MeshStandardMaterial({
    color: 0xf2efe7,
    roughness: 0.72,
  });
  const bubbleMaterial = new THREE.MeshStandardMaterial({
    color: 0xf7fbfc,
    roughness: 0.05,
    transparent: true,
    opacity: 0.95,
    emissive: 0x000000,
    emissiveIntensity: 0,
  });

  // The middle Wet Lab island already contains the retort base and rod.
  // This generated group only adds the clamp and experiment apparatus.
  const clamp = mesh(
    new THREE.BoxGeometry(0.19, 0.018, 0.018),
    metal,
    "TitrationBuretteClamp"
  );
  clamp.position.set(-0.085, 0.335, 0.04);
  root.add(clamp);

  const burette = mesh(
    new THREE.CylinderGeometry(0.0125, 0.0125, 0.58, 24, 1, true),
    glass,
    "TitrationBurette"
  );
  burette.position.set(0.015, 0.59, 0.04);
  addReadableEdges(burette, "reading", 0.9, focusEdges);
  burette.userData.interaction = { id: "burette-reading", kind: "reading" };
  root.add(burette);

  // Cylinder edges omit the vertical silhouette; give the narrow tube readable rails.
  const tubeOutlineMaterial = new THREE.MeshBasicMaterial({ color: 0x456269 });
  for (const side of [-1, 1]) {
    const rail = mesh(
      new THREE.CylinderGeometry(0.0009, 0.0009, 0.58, 8),
      tubeOutlineMaterial,
      `TitrationBuretteOutline-${side}`
    );
    rail.position.set(0.015 + side * 0.011, 0.59, 0.049);
    rail.castShadow = false;
    root.add(rail);
  }

  const meniscus = mesh(
    new THREE.LatheGeometry(
      [
        new THREE.Vector2(0, 0),
        new THREE.Vector2(0.004, 0.0004),
        new THREE.Vector2(0.007, 0.0012),
        new THREE.Vector2(0.0085, 0.0025),
      ],
      32
    ),
    new THREE.MeshBasicMaterial({
      color: 0xe6f0ed,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
    }),
    "TitrationBuretteMeniscus"
  );
  meniscus.castShadow = false;
  meniscus.renderOrder = 2;
  root.add(meniscus);

  const buretteLiquid = mesh(
    new THREE.CylinderGeometry(0.0085, 0.0085, BURETTE_LIQUID_HEIGHT_M, 18),
    buretteLiquidMaterial,
    "TitrationBuretteLiquid"
  );
  buretteLiquid.position.set(
    0.015,
    BURETTE_LIQUID_BOTTOM_M + BURETTE_LIQUID_HEIGHT_M / 2,
    0.04
  );
  buretteLiquid.renderOrder = 1;
  root.add(buretteLiquid);

  let scaleLabelCount = 0;
  for (let millilitre = 0; millilitre <= 25; millilitre += 1) {
    const major = millilitre % 5 === 0;
    const y = THREE.MathUtils.lerp(
      BURETTE_SCALE_TOP_M,
      BURETTE_SCALE_BOTTOM_M,
      millilitre / 25
    );
    const tick = mesh(
      new THREE.BoxGeometry(major ? 0.04 : 0.025, 0.0026, 0.0034),
      scaleMaterial,
      `TitrationBuretteScaleTick-${millilitre}`
    );
    tick.position.set(0.015, y, 0.054);
    root.add(tick);

    if (major) {
      const label = createScaleLabel(String(millilitre), ownedTextures);
      if (label) {
        label.position.set(0.062, y, 0.057);
        root.add(label);
        scaleLabelCount += 1;
      }
    }
  }

  // The spindle stays fixed; the handle turns in the visible vertical plane.
  const stopcockBody = mesh(
    new THREE.CylinderGeometry(0.014, 0.014, 0.036, 24),
    glass,
    "TitrationStopcockBody"
  );
  stopcockBody.rotation.x = Math.PI / 2;
  stopcockBody.position.set(0.015, 0.285, 0.04);
  root.add(stopcockBody);

  const tap = new THREE.Group();
  tap.name = "TitrationBuretteTap";
  tap.position.set(0.015, 0.285, 0.065);
  tap.userData.interaction = {
    id: "burette-tap",
    kind: "rotary-control",
  };
  const spindle = mesh(
    new THREE.CylinderGeometry(0.009, 0.009, 0.016, 20),
    darkMetal,
    "TitrationStopcockSpindle"
  );
  spindle.rotation.x = Math.PI / 2;
  tap.add(spindle);
  const handle = mesh(
    new THREE.BoxGeometry(0.075, 0.011, 0.014),
    darkMetal,
    "TitrationStopcockHandle"
  );
  tap.add(handle);
  for (const side of [-1, 1]) {
    const grip = mesh(
      new THREE.SphereGeometry(0.009, 12, 8),
      darkMetal,
      `TitrationStopcockGrip-${side}`
    );
    grip.position.x = side * 0.034;
    grip.scale.set(1, 0.85, 1);
    tap.add(grip);
  }
  root.add(tap);

  const tapHitTarget = new THREE.Mesh(
    new THREE.BoxGeometry(0.14, 0.09, 0.1),
    new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0,
      depthWrite: false,
    })
  );
  tapHitTarget.name = "TitrationBuretteTapHitTarget";
  tapHitTarget.position.copy(tap.position);
  tapHitTarget.userData.interaction = tap.userData.interaction;
  root.add(tapHitTarget);

  const flowDrop = mesh(
    new THREE.SphereGeometry(0.0025, 16, 12),
    buretteLiquidMaterial,
    "TitrationFlowDrop"
  );
  flowDrop.position.set(0.015, BURETTE_OUTLET_Y_M, 0.04);
  flowDrop.visible = false;
  root.add(flowDrop);

  const tip = mesh(
    new THREE.LatheGeometry(
      [
        new THREE.Vector2(0.0012, 0),
        new THREE.Vector2(0.0022, 0),
        new THREE.Vector2(0.0024, 0.015),
        new THREE.Vector2(0.0032, 0.045),
        new THREE.Vector2(0.0055, 0.08),
        new THREE.Vector2(0.0045, 0.08),
        new THREE.Vector2(0.0022, 0.045),
        new THREE.Vector2(0.0014, 0.015),
        new THREE.Vector2(0.0012, 0),
      ],
      32
    ),
    glass,
    "TitrationBuretteTip"
  );
  tip.position.set(0.015, BURETTE_OUTLET_Y_M, 0.04);
  const tipEdges = addReadableEdges(tip, "tip", 0.9, focusEdges);
  // Smooth lathe geometry has no vertical silhouette in EdgesGeometry.
  for (const side of [-1, 1]) {
    const silhouette = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(side * 0.0022, 0, 0.001),
        new THREE.Vector3(side * 0.0024, 0.015, 0.001),
        new THREE.Vector3(side * 0.0032, 0.045, 0.001),
        new THREE.Vector3(side * 0.0055, 0.08, 0.001),
      ]),
      tipEdges.material
    );
    silhouette.name = `TitrationBuretteTipOutline-${side}`;
    tip.add(silhouette);
  }
  root.add(tip);

  const bubble = mesh(
    new THREE.SphereGeometry(0.0018, 16, 12),
    bubbleMaterial,
    "TitrationTipAirBubble"
  );
  bubble.position.set(0.015, 0.235, 0.04);
  root.add(bubble);

  const whiteTile = mesh(
    new THREE.BoxGeometry(0.19, 0.006, 0.19),
    whiteTileMaterial,
    "TitrationWhiteTile"
  );
  whiteTile.position.set(0.015, 0.004, 0.04);
  addReadableEdges(whiteTile, "flask", 0.5, focusEdges);
  root.add(whiteTile);

  const flaskContactShadow = mesh(
    new THREE.CircleGeometry(0.085, 32),
    new THREE.MeshBasicMaterial({
      color: 0x24383c,
      transparent: true,
      opacity: 0.2,
      depthWrite: false,
    }),
    "TitrationFlaskContactShadow"
  );
  flaskContactShadow.rotation.x = -Math.PI / 2;
  flaskContactShadow.position.set(0.015, 0.008, 0.04);
  flaskContactShadow.scale.set(1.35, 0.75, 1);
  flaskContactShadow.castShadow = false;
  flaskContactShadow.receiveShadow = false;
  root.add(flaskContactShadow);

  const flaskBody = mesh(
    new THREE.CylinderGeometry(0.029, 0.067, 0.105, 32, 1, true),
    glass,
    "TitrationConicalFlask"
  );
  flaskBody.position.set(0.015, 0.064, 0.04);
  addReadableEdges(flaskBody, "flask", 0.9, focusEdges);
  flaskBody.userData.interaction = { id: "swirl-flask", kind: "mixing" };
  root.add(flaskBody);

  const flaskNeck = mesh(
    new THREE.CylinderGeometry(0.012, 0.015, 0.044, 24, 1, true),
    glass,
    "TitrationFlaskNeck"
  );
  flaskNeck.position.set(0.015, 0.14, 0.04);
  addReadableEdges(flaskNeck, "flask", 0.9, focusEdges);
  root.add(flaskNeck);

  const flaskShoulder = mesh(
    new THREE.CylinderGeometry(0.015, 0.029, 0.012, 32, 1, true),
    glass,
    "TitrationFlaskShoulder"
  );
  flaskShoulder.position.set(0.015, 0.116, 0.04);
  const flaskLip = mesh(
    new THREE.TorusGeometry(0.012, 0.0015, 8, 32),
    glass,
    "TitrationFlaskLip"
  );
  flaskLip.rotation.x = -Math.PI / 2;
  flaskLip.position.set(0.015, 0.162, 0.04);
  const flaskBase = mesh(
    new THREE.CylinderGeometry(0.067, 0.067, 0.003, 32),
    glass,
    "TitrationFlaskBase"
  );
  flaskBase.position.set(0.015, 0.012, 0.04);
  const clampGrip = mesh(
    new THREE.TorusGeometry(0.016, 0.0025, 8, 24),
    metal,
    "TitrationClampGrip"
  );
  clampGrip.rotation.x = -Math.PI / 2;
  clampGrip.position.set(0.015, 0.335, 0.04);
  root.add(clampGrip);

  const flaskLiquid = mesh(
    new THREE.CylinderGeometry(0.024, 0.054, 0.047, 28),
    flaskLiquidMaterial,
    "TitrationFlaskLiquid"
  );
  flaskLiquid.position.set(0.015, 0.038, 0.04);
  root.add(flaskLiquid);

  const flaskLiquidSurface = mesh(
    new THREE.CircleGeometry(0.026, 32),
    flaskLiquidSurfaceMaterial,
    "TitrationFlaskLiquidSurface"
  );
  flaskLiquidSurface.rotation.x = -Math.PI / 2;
  flaskLiquidSurface.position.set(0.015, 0.062, 0.04);
  flaskLiquidSurface.castShadow = false;
  root.add(flaskLiquidSurface);

  const flaskAssembly = new THREE.Group();
  flaskAssembly.name = "TitrationFlaskAssembly";
  for (const part of [
    flaskBody,
    flaskNeck,
    flaskShoulder,
    flaskLip,
    flaskBase,
    flaskLiquid,
    flaskLiquidSurface,
    flaskContactShadow,
  ]) {
    flaskAssembly.add(part);
  }
  root.add(flaskAssembly);

  const meniscusOverlayAnchor = new THREE.Object3D();
  meniscusOverlayAnchor.name = "TitrationMeniscusOverlayAnchor";
  root.add(meniscusOverlayAnchor);

  const tipOverlayAnchor = new THREE.Object3D();
  tipOverlayAnchor.name = "TitrationTipOverlayAnchor";
  tipOverlayAnchor.position.set(0.015, 0.205, 0.04);
  root.add(tipOverlayAnchor);

  const tapOverlayAnchor = new THREE.Object3D();
  tapOverlayAnchor.name = "TitrationTapOverlayAnchor";
  tapOverlayAnchor.position.copy(tap.position);
  root.add(tapOverlayAnchor);

  const flaskNeckOverlayAnchor = new THREE.Object3D();
  flaskNeckOverlayAnchor.name = "TitrationFlaskNeckOverlayAnchor";
  flaskNeckOverlayAnchor.position.set(0.015, 0.16, 0.04);
  flaskAssembly.add(flaskNeckOverlayAnchor);

  const flaskBodyOverlayAnchor = new THREE.Object3D();
  flaskBodyOverlayAnchor.name = "TitrationFlaskBodyOverlayAnchor";
  flaskBodyOverlayAnchor.position.set(0.015, 0.075, 0.04);
  flaskAssembly.add(flaskBodyOverlayAnchor);

  const recordOverlayAnchor = new THREE.Object3D();
  recordOverlayAnchor.name = "TitrationRecordOverlayAnchor";
  recordOverlayAnchor.position.set(0.095, 0.025, 0.04);
  root.add(recordOverlayAnchor);

  const apparatusLightTarget = new THREE.Object3D();
  apparatusLightTarget.name = "TitrationApparatusLightTarget";
  apparatusLightTarget.position.set(0, 0.32, 0);
  root.add(apparatusLightTarget);

  const apparatusKeyLight = new THREE.SpotLight(
    0xf4ffff,
    1.35,
    3,
    Math.PI / 5,
    0.5,
    1.2
  );
  apparatusKeyLight.name = "TitrationApparatusKeyLight";
  apparatusKeyLight.position.set(0.65, 1.25, 0.8);
  apparatusKeyLight.target = apparatusLightTarget;
  root.add(apparatusKeyLight);

  const apparatusRimLight = new THREE.PointLight(0xbfeef4, 0.55, 1.8, 1.5);
  apparatusRimLight.name = "TitrationApparatusRimLight";
  apparatusRimLight.position.set(-0.35, 0.7, -0.35);
  root.add(apparatusRimLight);

  const overlayWorldAnchors: TitrationOverlayWorldAnchors = {
    meniscus: new THREE.Vector3(),
    tip: new THREE.Vector3(),
    tap: new THREE.Vector3(),
    flaskNeck: new THREE.Vector3(),
    flaskBody: new THREE.Vector3(),
    record: new THREE.Vector3(),
  };
  const apparatusObjects = [
    burette,
    tip,
    flaskBody,
    flaskNeck,
    flaskLiquid,
    flaskLiquidSurface,
    whiteTile,
  ];

  let visual = deriveTitrationWorkstationVisualState({
    dispensedMl: 0,
    tapOpenFraction: 0,
    airBubblePresent: true,
    eyeLevel: true,
    indicatorAdded: false,
    swirled: false,
    trials: [],
    answer: "",
    analysisFeedback: null,
  });

  function applyOverlayFocus() {
    for (const edge of focusEdges) {
      const focused = overlayFocus !== null && edge.group === overlayFocus;
      edge.material.color.setHex(
        focused ? FOCUSED_EDGE_COLOR : GLASS_EDGE_COLOR
      );
      edge.material.opacity = focused ? 1 : edge.normalOpacity;
    }

    const tipFocused = overlayFocus === "tip";
    bubbleMaterial.emissive.setHex(tipFocused ? 0x3a747c : 0x000000);
    bubbleMaterial.emissiveIntensity = tipFocused ? 0.5 : 0;

    const tapFocused = overlayFocus === "tap";
    darkMetal.emissive.setHex(tapFocused ? 0x8a4e13 : 0x000000);
    darkMetal.emissiveIntensity = tapFocused ? 0.2 : 0;

    const readingFocused = overlayFocus === "reading";
    buretteLiquidMaterial.emissive.setHex(readingFocused ? 0x53635e : 0x000000);
    buretteLiquidMaterial.emissiveIntensity = readingFocused ? 0.08 : 0;

    const flaskFocused = overlayFocus === "flask";
    flaskLiquidMaterial.emissive.setHex(flaskFocused ? 0x12383d : 0x000000);
    flaskLiquidMaterial.emissiveIntensity = flaskFocused ? 0.32 : 0;
    flaskLiquidSurfaceMaterial.emissive.setHex(
      flaskFocused ? 0x1f5860 : 0x000000
    );
    flaskLiquidSurfaceMaterial.emissiveIntensity = flaskFocused ? 0.42 : 0;
  }

  function setOverlayFocus(focus: TitrationOverlayFocus) {
    if (focus === overlayFocus) return;
    overlayFocus = focus;
    applyOverlayFocus();
  }

  let previousSession: TitrationSessionState | null = null;
  let deliveryMl = 0;
  let dropElapsed = 0;
  let dropActive = false;
  let swirlRemaining = 0;

  function update(
    session: TitrationSessionState,
    deltaSeconds = 0,
    reducedMotion = false
  ) {
    const elapsed = Math.max(0, Math.min(0.05, deltaSeconds));
    if (previousSession) {
      const delivered = session.dispensedMl - previousSession.dispensedMl;
      if (delivered < 0) {
        deliveryMl = 0;
        dropElapsed = 0;
        dropActive = false;
        swirlRemaining = 0;
      } else if (delivered > 0)
        deliveryMl = Math.min(0.4, deliveryMl + delivered);
      // Continuous delivery can outpace the decorative drop animation. Closing
      // the tap discards that queue; only a drop already in flight may finish.
      // A one-drop action keeps the tap closed on both sides of the transition.
      if (previousSession.tapOpenFraction > 0 && session.tapOpenFraction === 0)
        deliveryMl = dropActive ? 0 : Math.min(0.05, Math.max(0, delivered));
      if (!previousSession.swirled && session.swirled && delivered >= 0)
        swirlRemaining = 1.2;
    }
    previousSession = session;
    if (!root.visible || reducedMotion) {
      deliveryMl = 0;
      dropElapsed = 0;
      dropActive = false;
      swirlRemaining = 0;
    }

    visual = deriveTitrationWorkstationVisualState(session);
    const liquidHeight = BURETTE_LIQUID_HEIGHT_M * visual.buretteFillFraction;
    buretteLiquid.scale.y = visual.buretteFillFraction;
    buretteLiquid.position.y = BURETTE_LIQUID_BOTTOM_M + liquidHeight / 2;
    meniscusOverlayAnchor.position.set(
      0.015,
      BURETTE_LIQUID_BOTTOM_M + liquidHeight,
      0.04
    );
    meniscus.position.copy(meniscusOverlayAnchor.position);
    tap.rotation.z = visual.tapOpenFraction * Math.PI * 0.5;
    bubble.visible = visual.bubbleVisible;
    if (!dropActive && deliveryMl >= 0.05 - 1e-8) {
      deliveryMl = Math.max(0, deliveryMl - 0.05);
      dropElapsed = 0;
      dropActive = true;
    }
    if (dropActive) {
      dropElapsed += elapsed;
      if (dropElapsed >= 0.18) {
        dropActive = false;
        dropElapsed = 0;
      }
    }
    flowDrop.visible = !reducedMotion && root.visible && dropActive;
    flowDrop.position.y =
      BURETTE_OUTLET_Y_M - (dropElapsed / 0.18) * (BURETTE_OUTLET_Y_M - 0.065);
    flowDrop.scale.set(0.75, 1.2, 0.75);
    swirlRemaining = Math.max(0, swirlRemaining - elapsed);
    const swirlPhase = (1.2 - swirlRemaining) * Math.PI * 5;
    const swirlEnvelope = Math.sin((Math.PI * swirlRemaining) / 1.2);
    flaskAssembly.position.set(
      Math.cos(swirlPhase) * 0.005 * swirlEnvelope,
      0,
      Math.sin(swirlPhase) * 0.005 * swirlEnvelope
    );
    flaskLiquidSurface.rotation.z = Math.sin(swirlPhase) * 0.08 * swirlEnvelope;

    flaskLiquidMaterial.color.setHex(visual.flaskColor);
    flaskLiquidMaterial.opacity = visual.flaskOpacity;
    flaskLiquidSurfaceMaterial.color.setHex(visual.flaskColor);
    flaskLiquidSurfaceMaterial.opacity = Math.min(
      0.96,
      visual.flaskOpacity + 0.14
    );
  }

  function getOverlayWorldAnchors(): TitrationOverlayWorldAnchors {
    meniscusOverlayAnchor.getWorldPosition(overlayWorldAnchors.meniscus);
    tipOverlayAnchor.getWorldPosition(overlayWorldAnchors.tip);
    tapOverlayAnchor.getWorldPosition(overlayWorldAnchors.tap);
    flaskNeckOverlayAnchor.getWorldPosition(overlayWorldAnchors.flaskNeck);
    flaskBodyOverlayAnchor.getWorldPosition(overlayWorldAnchors.flaskBody);
    recordOverlayAnchor.getWorldPosition(overlayWorldAnchors.record);
    return overlayWorldAnchors;
  }

  function getApparatusWorldBounds(target = new THREE.Box3()) {
    target.makeEmpty();
    for (const object of apparatusObjects) target.expandByObject(object);
    return target;
  }

  function getDebugState(): TitrationWorkstationDebugState {
    const representativeEdge =
      focusEdges.find((edge) => edge.group === "reading") ?? focusEdges[0];
    return {
      ...visual,
      rootPosition: root.position
        .toArray()
        .map((value) => Number(value.toFixed(4))) as [number, number, number],
      rootVisible: root.visible,
      overlayFocus,
      glassMaterialKind: glass.type,
      glassTransmission: glass.transmission,
      glassOpacity: glass.opacity,
      glassEdgeOpacity: representativeEdge?.material.opacity ?? 0,
      buretteLiquidOpacity: buretteLiquidMaterial.opacity,
      flaskSurfaceOpacity: flaskLiquidSurfaceMaterial.opacity,
      contactShadowVisible: flaskContactShadow.visible,
      scaleLabelCount,
      motion: {
        dropVisible: flowDrop.visible,
        dropY: flowDrop.position.y,
        swirlRemaining,
        flaskOffset: flaskAssembly.position.toArray(),
        surfaceTilt: flaskLiquidSurface.rotation.z,
      },
    };
  }

  function getTapWorldPosition(target = new THREE.Vector3()) {
    return tap.getWorldPosition(target);
  }

  function getBuretteWorldPosition(target = new THREE.Vector3()) {
    return burette.getWorldPosition(target);
  }

  function getFlaskWorldPosition(target = new THREE.Vector3()) {
    return flaskBody.getWorldPosition(target);
  }

  function getFlaskLiquidSurfaceWorldPosition(target = new THREE.Vector3()) {
    return flaskLiquidSurface.getWorldPosition(target);
  }

  function dispose() {
    for (const texture of ownedTextures) texture.dispose();
    root.traverse((object) => {
      const child = object as THREE.Mesh & {
        isLineSegments?: boolean;
        isSprite?: boolean;
      };
      if (!child.isMesh && !child.isLineSegments && !child.isSprite) return;
      child.geometry?.dispose();
      const materials = Array.isArray(child.material)
        ? child.material
        : [child.material];
      materials.forEach((material) => material?.dispose());
    });
  }

  update({
    dispensedMl: 0,
    tapOpenFraction: 0,
    airBubblePresent: true,
    eyeLevel: true,
    indicatorAdded: false,
    swirled: false,
    trials: [],
    answer: "",
    analysisFeedback: null,
  });

  // The first external session is synchronization, not a delivery action.
  previousSession = null;

  return {
    root,
    update,
    setOverlayFocus,
    getOverlayWorldAnchors,
    getApparatusWorldBounds,
    getDebugState,
    getTapWorldPosition,
    getBuretteWorldPosition,
    getFlaskWorldPosition,
    getFlaskLiquidSurfaceWorldPosition,
    dispose,
  };
}
